// The "everything your room needs" checklist — docs/plans/2026-09-15-gear-v2-brief.md
// §2 "Rows". Pure: reads the document, the catalogue and an already-computed
// BuildReport (from checkBuild) and returns display rows plus a percentage.
// The web layer only renders what this returns.

import type { EditorDoc, MediaEntry } from '@roomtreatment/core';
import type { Product } from '@roomtreatment/av-catalog';
import type { BuildContext } from './context';
import { buildContext } from './context';
import { channelGroup } from './channels';
import { productName } from './copy';
import { genericDisplayName, mediaProductId, type MediaPart } from './media';
import { roomRequirementsFromContext, chainFitForChecklist } from './requirements';
import type {
  BuildReport, ChecklistRowAction, ChecklistRowState, ChecklistSection, ChecklistTier,
  GearRailCategory, RoomChecklistRow, RoomChecklistResult,
} from './types';

export interface RoomChecklistOptions {
  hasRoomResults: boolean;
  report: BuildReport;
}

const PRJ_DISTANCE_RULE = 'PRJ-01';

const CHANNEL_RULES: readonly string[] = ['AVR-01', 'AVR-02', 'AVR-03', 'AVR-04'];
const POWER_RULES: readonly string[] = ['IMP-01', 'PWR-02'];

/** Rules already surfaced by a dedicated row — every other issue becomes an
 *  "unmodelled" extra row (brief: "any issue from the existing rules that no
 *  row covers becomes an extra failing row in its section"). */
const COVERED_RULES = new Set<string>([...CHANNEL_RULES, ...POWER_RULES, PRJ_DISTANCE_RULE]);

/** Section an unmodelled rule's issue surfaces in. LAY (speaker angle) and
 *  PHY (rear port, front-speaker match) are physical/model-matching
 *  problems with the speakers themselves; CON-02 is a sub-to-receiver
 *  wiring mismatch, an electronics compatibility fact. */
const EXTRA_RULE_SECTION: Record<string, ChecklistSection> = {
  'LAY-02': 'Sound',
  'LAY-03': 'Sound',
  'PHY-02': 'Sound',
  'PHY-05': 'Sound',
  'CON-02': 'Amp',
};

/** An unfinished row blocks the setup when its tier is required and is a
 *  suggestion otherwise. Rows whose answer depends on the build decide it
 *  inline instead. */
function openState(tier: ChecklistTier): ChecklistRowState {
  return tier === 'required' ? 'blocking' : 'suggestion';
}

function findIssue(report: BuildReport, ruleIds: readonly string[]) {
  return report.issues.find((i) => ruleIds.includes(i.rule as string));
}

function manualDone(doc: EditorDoc, id: string): boolean {
  return (doc.checklist?.done ?? []).includes(id);
}

function speakerProductName(ctx: BuildContext, speakerId: string): string | undefined {
  const entry = ctx.doc.speakers.find((s) => s.id === speakerId);
  const product = entry?.productId ? ctx.catalog.get(entry.productId) : undefined;
  return product ? productName(product) : undefined;
}

/** Whether every channelled speaker in `ids` (a channel group, or the
 *  subwoofer ids) is bound to a catalogue product, plus one representative
 *  id/name for the row's action and sub-line. Empty `ids` (no such channel
 *  in the current layout) reads as "not done" — the row stays visible and
 *  to-do, per brief: "everything your room needs" doesn't hide behind the
 *  layout picker (see also brief §1, add-anywhere). */
function groupBinding(ctx: BuildContext, ids: readonly string[]): { done: boolean; id?: string; name?: string } {
  if (ids.length === 0) return { done: false };
  let allBound = true;
  let repId: string | undefined;
  let repName: string | undefined;
  for (const id of ids) {
    const name = speakerProductName(ctx, id);
    if (name) {
      repId ??= id;
      repName ??= name;
    } else {
      allBound = false;
    }
  }
  return { done: allBound, id: repId, name: repName };
}

function idsForGroups(ctx: BuildContext, groups: readonly string[]): string[] {
  return [...ctx.bedIds, ...ctx.overheadIds].filter((id) => {
    const g = channelGroup(ctx.channels[id]);
    return g != null && groups.includes(g);
  });
}

function gearRailAction(category: GearRailCategory): ChecklistRowAction {
  return { kind: 'open-gear-rail', category };
}

/** A "bound" row's action: select the object once it exists, otherwise open
 *  the rail on its category (brief: "a 'not done' speaker, electronics or
 *  picture row opens the Gear rail on its category"; done rows select). */
function boundRowAction(done: boolean, id: string | undefined, category: GearRailCategory): ChecklistRowAction | undefined {
  if (done && id) return { kind: 'select', id };
  if (!done) return gearRailAction(category);
  return undefined;
}

export function roomChecklist(
  doc: EditorDoc,
  catalog: ReadonlyMap<string, Product>,
  opts: RoomChecklistOptions,
): RoomChecklistResult {
  const ctx = buildContext(doc, catalog, { hasRoomResults: opts.hasRoomResults });
  const { report } = opts;
  const rows: RoomChecklistRow[] = [];

  // ── Room ───────────────────────────────────────────────────────────────
  rows.push({
    id: 'room:listener',
    section: 'Room',
    title: 'Where you sit',
    sub: ctx.listener ? undefined : 'Changes the bass more than any speaker',
    state: ctx.listener ? 'done' : 'suggestion',
    // The setup plays without a seat placed here; the seat sharpens the advice.
    tier: 'recommended',
    manual: false,
    action: ctx.listener ? { kind: 'select', id: ctx.listener.id } : undefined,
  });
  rows.push({
    id: 'room:treatment',
    section: 'Room',
    title: 'Acoustic panels',
    sub: doc.panels.length >= 1
      ? `${doc.panels.length} panel${doc.panels.length === 1 ? '' : 's'}`
      : 'Tame the echo and the boom',
    state: doc.panels.length >= 1 ? 'done' : 'suggestion',
    tier: 'recommended',
    manual: false,
  });
  rows.push({
    id: 'room:simulated',
    section: 'Room',
    title: 'Run the simulation',
    state: opts.hasRoomResults ? 'done' : 'suggestion',
    tier: 'recommended',
    manual: false,
    action: opts.hasRoomResults ? undefined : { kind: 'compute' },
  });

  // ── Speakers ───────────────────────────────────────────────────────────
  const front = groupBinding(ctx, idsForGroups(ctx, ['front']));
  rows.push({
    id: 'speakers:front',
    section: 'Sound',
    title: 'Left and right speakers',
    sub: front.name,
    state: front.done ? 'done' : 'blocking',
    tier: 'required',
    manual: false,
    action: boundRowAction(front.done, front.id, 'speakers'),
  });

  const center = groupBinding(ctx, idsForGroups(ctx, ['center']));
  rows.push({
    id: 'speakers:center',
    section: 'Sound',
    title: 'Center speaker',
    sub: center.name ?? 'Carries almost all the dialogue',
    state: center.done ? 'done' : 'suggestion',
    tier: 'recommended',
    manual: false,
    action: boundRowAction(center.done, center.id, 'speakers'),
  });

  const surround = groupBinding(ctx, idsForGroups(ctx, ['surround', 'surround_back']));
  rows.push({
    id: 'speakers:surround',
    section: 'Sound',
    title: 'Surround speakers',
    sub: surround.name ?? 'Sound from beside and behind you',
    state: surround.done ? 'done' : 'suggestion',
    tier: 'recommended',
    manual: false,
    action: boundRowAction(surround.done, surround.id, 'speakers'),
  });

  const sub = groupBinding(ctx, ctx.subIds);
  rows.push({
    id: 'speakers:sub',
    section: 'Sound',
    title: 'Subwoofer',
    sub: sub.name ?? 'The biggest upgrade for films',
    state: sub.done ? 'done' : 'suggestion',
    tier: 'recommended',
    manual: false,
    action: boundRowAction(sub.done, sub.id, 'subwoofers'),
  });

  const height = groupBinding(ctx, idsForGroups(ctx, ['height1', 'height2']));
  rows.push({
    id: 'speakers:height',
    section: 'Sound',
    title: 'Overhead speakers',
    sub: height.name,
    state: height.done ? 'done' : 'suggestion',
    tier: 'optional',
    manual: false,
    action: boundRowAction(height.done, height.id, 'speakers'),
  });

  // ── Electronics ────────────────────────────────────────────────────────
  // These three rows derive their state and sub-line from chainFit (the same
  // function the "what your room needs" block reads), so the two can never
  // disagree — docs/plans/2026-09-16-gear-requirements-brief.md §5.
  const req = roomRequirementsFromContext(doc, ctx);
  const fit = chainFitForChecklist(ctx, req);

  const receiverId = ctx.avUnit?.entry.id ?? ctx.amps[0]?.gearId;
  rows.push({
    id: 'electronics:receiver',
    section: 'Amp',
    title: 'Amp or receiver',
    sub: fit.frontEnd?.name ?? 'Powers the speakers and switches sources',
    // Active speakers carry their own amps: with no passive ones the setup
    // plays without a receiver, so a missing one is only a suggestion.
    state: fit.hasFrontEnd ? 'done' : req.passiveSpeakers === 0 && req.activeSpeakers > 0 ? 'suggestion' : 'blocking',
    tier: 'required',
    manual: false,
    action: boundRowAction(fit.hasFrontEnd, receiverId, 'electronics'),
  });

  // AVR-01 (height decoding) used to be bucketed with the channel/pre-out
  // rules under this same row (CHANNEL_RULES, above) — kept that way here:
  // "no height decoding" fails this row too, not a separate one.
  // Nothing to judge until a receiver or processor is placed: both rows are
  // left out then. They would only repeat the missing receiver as two more
  // blockers, and a tick would claim a check that never ran. 'Amp or
  // receiver' carries the fix.
  const judged = fit.hasFrontEnd;
  const channelsFail = judged && (fit.channels.have < fit.channels.need || fit.heights === 'no-decoding');
  if (judged) rows.push({
    id: 'electronics:channels',
    section: 'Amp',
    title: 'Enough channels for your speakers',
    sub: fit.fixLines.channels ?? fit.fixLines.heights,
    state: channelsFail ? 'blocking' : 'done',
    tier: 'required',
    manual: false,
    action: channelsFail ? gearRailAction('electronics') : undefined,
  });

  // IMP-01 (impedance) and PWR-02 (headroom) used to share this row
  // (POWER_RULES, above) — kept that way: an under-rated OR too-weak amp
  // both fail "Enough power for your speakers".
  const powerFails = judged && fit.power?.band === 'too-weak';
  const loadFails = judged && fit.load === 'too-low';
  if (judged) rows.push({
    id: 'electronics:power',
    section: 'Amp',
    title: 'Enough power for your speakers',
    sub: powerFails ? fit.fixLines.power : loadFails ? fit.fixLines.load : undefined,
    state: powerFails || loadFails ? 'blocking' : 'done',
    tier: 'required',
    manual: false,
    action: powerFails || loadFails ? gearRailAction('electronics') : undefined,
  });

  // ── Picture ────────────────────────────────────────────────────────────
  // The room's displays (`doc.media`). A room without one has no Picture
  // rows: a display is optional (it is added from the System view's Media
  // block), so its absence is never a to-do on every stereo room.
  const displays = doc.media ?? [];
  const nameOf = (entry: MediaEntry, part: MediaPart): string => {
    const id = mediaProductId(entry, part);
    const product = id ? ctx.catalog.get(id) : undefined;
    return product ? productName(product) : genericDisplayName(entry, part);
  };
  if (displays[0]) {
    rows.push({
      id: 'picture:display',
      section: 'Picture',
      title: 'TV or projector',
      sub: nameOf(displays[0], 'device'),
      state: 'done',
      tier: 'recommended',
      manual: false,
      action: { kind: 'select', id: displays[0].id },
    });
  }

  const setup = displays.find((m) => m.kind === 'projector');
  if (setup) {
    rows.push({
      id: 'picture:screen',
      section: 'Picture',
      title: 'Screen',
      sub: setup.screen ? nameOf(setup, 'screen') : undefined,
      state: setup.screen ? 'done' : 'blocking',
      tier: 'required',
      manual: false,
      action: setup.screen ? { kind: 'select', id: setup.id } : gearRailAction('screens'),
    });

    // Only a projector model publishes the throw ratio the check needs.
    if (setup.screen && ctx.projector?.entry.id === setup.id) {
      const distanceIssue = findIssue(report, [PRJ_DISTANCE_RULE]);
      rows.push({
        id: 'picture:distance',
        section: 'Picture',
        title: 'Projector fits the room',
        sub: distanceIssue?.text,
        state: distanceIssue ? 'blocking' : 'done',
        tier: 'required',
        manual: false,
        // The fix is where it hangs, so the row picks the projector up.
        action: distanceIssue ? { kind: 'select', id: setup.id } : undefined,
      });
    }
  }

  // A "Source" row (streamer, console, player) and an "HDMI cables" row both
  // shipped here and were cut: everyone building a theatre already owns a
  // source and knows a display needs an HDMI lead, so both rows only diluted
  // the list. Speaker wire and the sub cable were rows too, ticked by hand —
  // but a cable is a part you buy, with a gauge and a length the layout
  // decides, so they live with the parts now (`cableNeeds`, the System
  // view's Cables rows and its order), not as boxes to tick (owner,
  // 2026-09-27).

  // ── Set up ─────────────────────────────────────────────────────────────
  const roomCorrection = ctx.avUnit?.specs.roomCorrection?.v;
  if (roomCorrection) {
    rows.push({
      id: 'setup:room-correction',
      section: 'Set up',
      title: 'Run room correction',
      sub: roomCorrection,
      state: manualDone(doc, 'setup:room-correction') ? 'done' : 'suggestion',
      tier: 'recommended',
      manual: true,
      action: { kind: 'toggle' },
    });
  }

  // ── Unmodelled rules ───────────────────────────────────────────────────
  for (const issue of report.issues) {
    const rule = issue.rule as string;
    if (COVERED_RULES.has(rule)) continue;
    const section = EXTRA_RULE_SECTION[rule] ?? 'Sound';
    const tier: ChecklistTier = issue.severity === 'error' ? 'required' : 'recommended';
    rows.push({
      id: `extra:${issue.id}`,
      section,
      title: issue.text,
      state: openState(tier),
      tier,
      manual: false,
      action: gearRailAction(section === 'Amp' ? 'electronics' : 'speakers'),
    });
  }

  const countable = rows.filter((r) => r.tier !== 'optional');
  const done = countable.filter((r) => r.state === 'done').length;
  const percent = countable.length === 0 ? 100 : Math.round((done / countable.length) * 100);

  return { rows, percent };
}
